import React from 'react';
import { useParams } from 'react-router-dom';
import { useTutorials } from '../../hooks/useTutorials';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import Breadcrumb from '../../components/Breadcrumb';
import TutorialCard from '../../components/tutorials/TutorialCard';
import { generateTutorialCategoryBreadcrumb } from '../../utils/breadcrumb';
import { Loader2, BookOpen } from 'lucide-react';

export default function TutorialsByCategoryPage() {
  const { categorySlug, potentialSlug } = useParams<{ categorySlug?: string; potentialSlug?: string }>();
  
  // Use categorySlug from explicit route or potentialSlug from fallback route  
  const actualCategorySlug = categorySlug || potentialSlug;
  
  // Memoize the orderBy object to prevent infinite re-renders
  const orderBy = React.useMemo(() => ({ 
    column: 'order', 
    ascending: true 
  }), []);
  
  const { tutorials, loading, error } = useTutorials(actualCategorySlug, orderBy);
  const { setItems } = useBreadcrumb();

  // Format category name for display
  const formatCategoryName = (slug: string) => {
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const categoryName = actualCategorySlug ? formatCategoryName(actualCategorySlug) : 'Unknown Category';

  React.useEffect(() => {
    if (actualCategorySlug) {
      setItems(generateTutorialCategoryBreadcrumb(categoryName, actualCategorySlug));
    }
  }, [actualCategorySlug, categoryName, setItems]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb />
        
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="h-8 w-8 text-primary-500" />
            <h1 className="text-3xl font-bold">{categoryName} Tutorials</h1>
          </div>
          <p className="text-gray-600 text-lg">
            Step-by-step guides specifically for {categoryName.toLowerCase()}.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          </div>
        ) : error ? (
          <div className="text-red-600 bg-red-50 p-4 rounded-lg">
            Error loading tutorials: {error}
          </div>
        ) : tutorials.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">No tutorials available for this category yet.</p>
            <p className="text-gray-500">Check back soon for new content!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tutorials.map((tutorial) => (
              <TutorialCard key={tutorial.id} tutorial={tutorial} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}